<template>
  <div class="order text-neutral-500">
    <div class="bg-white mb-40">
      <div class="flex mdmax:flex-col justify-between gap-4 mdmax:gap-6 items-center mdmax:items-start p-6 shadow-border-b">
        <div class="flex mdmax:flex-col items-start gap-4 mdmax:gap-3 w-1/2 mdmax:w-full">
          <div class="flex-shrink-0">
            <span class="inline-flex justify-center items-center px-3 py-1.5 text-xs font-semibold rounded-md shadow-sm" :style="'backgroundColor: ' + orderDetail.status_color + '; color: white;'">{{
              orderDetail.status_display
            }}</span>
          </div>
          <div class="w-full min-w-0">
            <span class="text-neutral-500 text-xs block mb-2">{{ orderDetail.created_time_display }} / {{ orderDetail.order_id }}</span>
            <div class="flex items-center flex-wrap gap-2">
              <div class="bg-neutral-100 border border-neutral-200 rounded text-xs px-2 py-1 max-w-full">
                <span class="text-neutral-500">AWB Linknau:</span> 
                <strong class="text-neutral-800 break-all ml-1">{{ orderDetail.order_delivery?.awb }}</strong>
              </div>
              <div v-if="orderDetail.order_delivery?.external_awb" class="bg-neutral-100 border border-neutral-200 rounded text-xs px-2 py-1 max-w-full">
                <span class="text-neutral-500">{{ $t('order.orderDetail.awbPartner') }}:</span> 
                <strong class="text-neutral-800 break-all ml-1">{{ orderDetail.order_delivery?.external_awb }}</strong>
              </div>
            </div>
          </div>
        </div>
        <div class="w-1/2 mdmax:w-full flex items-center mdmax:items-stretch gap-3 justify-end mdmax:justify-start flex-wrap mdmax:flex-col">
          <Button v-if="canCancelOrder()" buttonText="Batalkan Pesanan" size="small" type="delete_outline" @action="toggleModalCancelOrder()" class="mdmax:w-full" />
          <Button :buttonText="$t('order.orderDetail.submitComplaint')" size="small" type="secondary" @action="toggleComplainModal()" class="mdmax:w-full" />
          <div
            class="flex items-center mdmax:flex-col mdmax:items-stretch gap-4 mdmax:gap-3 mdmax:w-full"
            v-if="orderDetail?.payment_info?.status === 'settlement' || orderDetail?.payment_info?.status === 'capture' || orderDetail?.status === 'OHUBCEK' || orderDetail?.status === 'OPRCD'"
          >
            <Button buttonText="View Receipt" size="small" @action="previewReceipt()" class="mdmax:w-full" />
            <Button buttonText="Download Shipping Label" size="small" :icon="() => import('@/components/Icons/Download')" @action="download()" class="mdmax:w-full" />
          </div>
          <div class="mdmax:w-full" v-if="orderDetail.status === 'OPAYWAIT'">
            <Button :buttonText="$t('order.orderDetail.continuePayment')" size="small" @action="doRetryPayment()" class="mdmax:w-full" />
          </div>
        </div>
      </div>
      <div class="flex mdmax:flex-col">
        <div class="flex-1">
          <div class="p-6 shadow-border-b">
            <p class="text-xl font-bold mb-6 text-neutral-700">{{ $t('order.orderDetail.weightTypePackage') }}</p>
            <div
              class="flex flex-col gap-3 mb-4 border rounded-md px-3 py-3 bg-neutral-200 last:mb-0"
              v-for="(packageItem, index) in orderDetail?.order_delivery?.packages"
              :key="'package-' + index"
            >
              <div class="flex justify-start gap-6">
                <div class="w-2/5">
                  <p class="text-sm font-medium mb-1 text-neutral-700">{{ $t('order.orderDetail.weight') }}</p>
                  <p>{{ packageItem.weight }}kg</p>
                </div>
                <div class="w-3/5">
                  <p class="text-sm font-medium mb-1 text-neutral-700">{{ $t('order.orderDetail.type') }}</p>
                  <p>{{ packageItem.category }}</p>
                </div>
              </div>
              <div class="flex justify-start gap-6 border-t border-neutral-300 pt-3" v-if="packageItem.dimension">
                <div class="w-full">
                  <p class="text-sm font-medium mb-1 text-neutral-700">Volume Paket</p>
                  <p>{{ packageItem.dimension.replace(/x/g, ' x ') }} cm <span class="text-neutral-500 font-medium">({{ getVolumetricWeight(packageItem.dimension) }} kg)</span></p>
                </div>
              </div>
            </div>
          </div>
          <div class="p-6 shadow-border-b">
            <p class="text-xl font-bold mb-6 text-neutral-700">{{ $t('order.orderDetail.deliveryService') }}</p>
            <div class="flex items-center gap-6 mb-6">
              <img :src="orderDetail.order_delivery?.service_logo" class="h-16 w-36 object-contain object-left" v-if="orderDetail.order_delivery?.service_logo" />
              <LogisticPlaceholder :logisticName="orderDetail.order_delivery?.service_name" v-else />
              <span class="text-sm text-secondary-500 bg-secondary-200 py-1 px-2 rounded-md" v-if="orderDetail.order_delivery?.service_name">{{ orderDetail.order_delivery?.service_name }}</span>
            </div>
            <div class="flex items-start gap-6">
              <div class="w-2/5">
                <p class="text-sm font-medium mb-1 text-neutral-700">{{ $t('order.orderDetail.serviceType') }}</p>
                <p>{{ orderDetail.order_delivery?.service_package_name || '-' }}</p>
              </div>
              <div class="w-3/5">
                <p class="text-sm font-medium mb-1 text-neutral-700">{{ $t('order.orderDetail.serviceType') }}</p>
                <p>{{ getServiceType(orderDetail.order_delivery?.service_type) || '-' }}</p>
              </div>
            </div>
          </div>
          <div class="p-6">
            <p class="text-xl font-bold mb-6 text-neutral-700">{{ $t('order.orderDetail.senderInfo') }}</p>
            <div class="flex justify-start gap-6 mb-6">
              <div class="w-2/5">
                <p class="text-sm font-medium mb-1 text-neutral-700">{{ $t('order.orderDetail.senderName') }}</p>
                <p>{{ orderDetail?.order_delivery?.sender?.name || '-' }}</p>
              </div>
              <div class="w-3/5">
                <p class="text-sm font-medium mb-1 text-neutral-700">{{ $t('order.orderDetail.senderPhone') }}</p>
                <p>{{ orderDetail?.order_delivery?.sender?.phone_number || '-' }}</p>
              </div>
            </div>
            <p class="text-sm font-medium mb-1 text-neutral-700">{{ $t('order.orderDetail.senderAddress') }}</p>
            <p>{{ orderDetail?.order_delivery?.sender?.address || '-' }}</p>
            <div class="mt-6 p-2 bg-neutral-200 rounded-md">
              <p class="text-sm font-medium mb-1 text-neutral-700">Detail Pickup</p>
              <p>{{ orderDetail?.order_delivery?.sender?.detail_address || '-' }}</p>
            </div>
            <div v-if="orderDetail?.order_delivery?.has_pod" class="mb-3 mt-6 flex items-center gap-4 w-full">
              <div class="text-sm w-full p-4 flex items-center gap-2 bg-secondary-100 text-neutral-700 rounded-md">
                <Info width="24" height="24" color="neutral-700" />{{ $t('order.orderDetail.pod') }}
              </div>
            </div>
          </div>
          <div class="p-6 mdmax:shadow-border-b">
            <p class="text-xl font-bold mb-6 text-neutral-700">{{ $t('order.orderDetail.receiverInfo') }}</p>
            <div class="flex justify-start gap-6 mb-6">
              <div class="w-2/5">
                <p class="text-sm font-medium mb-1 text-neutral-700">{{ $t('order.orderDetail.receiverName') }}</p>
                <p>{{ orderDetail?.order_delivery?.receiver?.name || '-' }}</p>
              </div>
              <div class="w-3/5">
                <p class="text-sm font-medium mb-1 text-neutral-700">{{ $t('order.orderDetail.receiverPhone') }}</p>
                <p>{{ orderDetail?.order_delivery?.receiver?.phone_number || '-' }}</p>
              </div>
            </div>
            <p class="text-sm font-medium mb-1 text-neutral-700">{{ $t('order.orderDetail.receiverAddress') }}</p>
            <p>{{ orderDetail?.order_delivery?.receiver?.address || '-' }}</p>
            <div class="mt-6 p-2 bg-neutral-200 rounded-md">
              <p class="text-sm font-medium mb-1 text-neutral-700">Detail Pickup</p>
              <p>{{ orderDetail?.order_delivery?.receiver?.detail_address || '-' }}</p>
            </div>
          </div>
        </div>
        <div class="flex-1 shadow-border-l mdmax:shadow-none">
          <div class="p-6 shadow-border-b">
            <p class="text-xl font-bold mb-6 text-neutral-700">{{ $t('order.orderDetail.packagePhoto') }}</p>
            <div class="mb-4" v-for="(paket, index) in orderDetail?.order_delivery?.packages" :key="'paket-' + index">
              <template v-if="paket.photo_from_courier">
                <img :src="paket.photo_from_courier" />
              </template>
              <p v-else>- {{ $t('order.orderDetail.photoNotAvailable') }} -</p>
            </div>
          </div>
          <div class="p-6 shadow-border-b" v-if="orderDetail?.price_adjustment">
            <p class="text-xl font-bold mb-6 text-neutral-700">{{ $t('order.orderDetail.priceAdjustment') }}</p>
            <div class="mb-6">
              <p class="text-sm font-medium mb-1 text-neutral-700">{{ $t('order.orderDetail.status') }}</p>
              <p>{{ orderDetail?.price_adjustment?.status || '-' }}</p>
            </div>
            <div class="flex justify-start gap-6 mb-6">
              <div class="w-2/5">
                <p class="text-sm font-medium mb-1 text-neutral-700">{{ $t('order.orderDetail.oldWeight') }}</p>
                <p>{{ orderDetail?.price_adjustment?.old_weight || '-' }} kg</p>
              </div>
              <div class="w-3/5">
                <p class="text-sm font-medium mb-1 text-neutral-700">{{ $t('order.orderDetail.newWeight') }}</p>
                <p>{{ orderDetail?.price_adjustment?.new_weight || '-' }} kg</p>
              </div>
            </div>
            <div class="flex justify-start gap-6 mb-6">
              <div class="w-2/5">
                <p class="text-sm font-medium mb-1 text-neutral-700">{{ $t('order.orderDetail.oldDimension') }}</p>
                <p>{{ orderDetail?.price_adjustment?.old_dimension || '-' }}</p>
              </div>
              <div class="w-3/5">
                <p class="text-sm font-medium mb-1 text-neutral-700">{{ $t('order.orderDetail.newDimension') }}</p>
                <p>{{ orderDetail?.price_adjustment?.new_dimension || '-' }}</p>
              </div>
            </div>
            <div class="flex justify-start gap-6 mb-6">
              <div class="w-2/5">
                <p class="text-sm font-medium mb-1 text-neutral-700">{{ $t('order.orderDetail.oldPrice') }}</p>
                <p>Rp {{ orderDetail?.price_adjustment?.old_price?.toLocaleString('id-ID') || '0' }}</p>
              </div>
              <div class="w-3/5">
                <p class="text-sm font-medium mb-1 text-neutral-700">{{ $t('order.orderDetail.newPrice') }}</p>
                <p>Rp {{ orderDetail?.price_adjustment?.new_price?.toLocaleString('id-ID') || '0' }}</p>
              </div>
            </div>
            <div class="mb-6 p-4 bg-secondary-50 border border-secondary-200 rounded-md" v-if="orderDetail?.price_adjustment?.status === 'Pending Additional Payment'">
              <p class="text-base font-bold mb-4 text-neutral-700">{{ $t('order.orderDetail.paymentAccountInfo') }}</p>
              <div class="flex justify-start gap-6 mb-3">
                <div class="w-2/5">
                  <p class="text-sm font-medium mb-1 text-neutral-700">{{ $t('order.orderDetail.accountName') }}</p>
                  <p class="font-semibold">LINKNOW PUTELLA DIGITAL</p>
                </div>
                <div class="w-3/5">
                  <p class="text-sm font-medium mb-1 text-neutral-700">{{ $t('order.orderDetail.accountBank') }}</p>
                  <p class="font-semibold">Mandiri</p>
                </div>
              </div>
              <div class="w-2/5">
                <p class="text-sm font-medium mb-1 text-neutral-700">{{ $t('order.orderDetail.accountNumberLabel') }}</p>
                <p class="font-semibold text-lg">1010011736731</p>
              </div>
            </div>
            <div class="flex justify-end gap-4" v-if="orderDetail?.price_adjustment?.status === 'Pending Additional Payment'">
              <Button :buttonText="$t('order.orderDetail.markAsPaid')" type="primary" size="small" @action="togglePriceAdjustmentModal()" />
            </div>
          </div>
          <div class="p-6 shadow-border-b">
            <p class="text-xl font-bold mb-6 text-neutral-700">{{ $t('order.orderDetail.paymentInfo') }}</p>
            <div class="mb-6">
              <p class="text-sm font-medium mb-1 text-neutral-700">{{ $t('order.orderDetail.status') }}</p>
              <p>{{ orderDetail?.payment_info?.status_display || '-' }}</p>
            </div>
            <div class="flex justify-start gap-6 mb-6">
              <div class="w-2/5">
                <p class="text-sm font-medium mb-1 text-neutral-700">{{ $t('order.orderDetail.paymentType') }}</p>
                <p>{{ orderDetail?.payment_info?.payment_method || '-' }}</p>
              </div>
              <div class="w-3/5">
                <p class="text-sm font-medium mb-1 text-neutral-700">{{ $t('order.orderDetail.bank') }}</p>
                <p>{{ orderDetail?.payment_info?.payment_provider || '-' }}</p>
              </div>
            </div>
            <div class="flex justify-start gap-6 mb-6">
              <div class="w-2/5">
                <p class="text-sm font-medium mb-1 text-neutral-700">{{ $t('order.orderDetail.accountNumber') }}</p>
                <p>{{ orderDetail?.payment_info?.payment_account_display || '-' }}</p>
              </div>
            </div>
            <!-- Voucher Info -->
            <div v-if="orderDetail?.payment_info?.voucher_info" class="mb-6">
              <p class="text-sm font-medium mb-3 text-neutral-700">Voucher Applied</p>
              <div class="mb-3">
                <p class="text-xs font-medium mb-1 text-neutral-700">Voucher Name</p>
                <p class="text-sm text-neutral-900">{{ orderDetail.payment_info.voucher_info.voucher_name }}</p>
              </div>
              <div class="flex justify-start gap-6 mb-3">
                <div class="w-2/5">
                  <p class="text-xs font-medium mb-1 text-neutral-700">Voucher Code</p>
                  <p class="text-sm text-neutral-900">{{ orderDetail.payment_info.voucher_info.voucher_code }}</p>
                </div>
                <div class="w-3/5">
                  <p class="text-xs font-medium mb-1 text-neutral-700">Discount Amount</p>
                  <p class="text-sm font-semibold text-green-600">- Rp {{ orderDetail.payment_info.voucher_info.voucher_amount | formatCurrency }}</p>
                </div>
              </div>
            </div>
            <!-- Price Info -->
            <div class="mb-6">
              <div v-if="orderDetail?.payment_info?.voucher_info" class="pt-3 border-t border-neutral-300">
                <div class="flex justify-between items-center mb-2">
                  <p class="text-xs text-neutral-700">Price Before Discount</p>
                  <p class="text-sm text-neutral-500 line-through">Rp {{ priceBeforeDiscount | formatCurrency }}</p>
                </div>
                <div class="flex justify-between items-center">
                  <p class="text-xs font-bold text-neutral-700">Price After Discount</p>
                  <p class="text-sm font-bold text-green-700">Rp {{ orderDetail.payment_info.price | formatCurrency }}</p>
                </div>
              </div>
              <div v-else>
                <p class="text-sm font-medium mb-1 text-neutral-700">Total Price</p>
                <p class="text-sm font-bold text-neutral-900">Rp {{ orderDetail?.payment_info?.price | formatCurrency }}</p>
              </div>
            </div>
          </div>
          <div class="p-6">
            <p class="text-xl font-bold mb-6 text-neutral-700">{{ $t('order.orderDetail.deliveryStatus') }}</p>
            <div class="flex gap-5" v-for="(tracking, index) in orderDetail?.tracking_infos" :key="'tracking' + index">
              <div class="line relative">
                <span class="inline-block w-5 h-5 rounded-full  border-4  relative z-1" :class="index === 0 ? 'bg-primary-500 border-primary-100' : 'bg-neutral-500 border-neutral-300'"></span>
                <span class="absolute border-dashed border-neutral-300 h-full w-px border left-1/2 transform -translate-x-1/2 top-0 z-0"></span>
              </div>
              <div class="status mb-6">
                <p class="text-sm mb-1">{{ tracking.time_display }}</p>
                <p class="text-neutral-700 mb-1">{{ tracking.main_info }}</p>
                <p class="text-sm">{{ tracking.additional_info }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- <div class="text-xs p-4 flex items-center gap-2 bg-secondary-100 text-neutral-700">
        <Info width="24" height="24" color="neutral-700" />Jangan lupa periksa kembali barang yang telah diterima sebelum memindahkan status order, pastikan sesuai dengan ukuran yang telah ditetapkan.
      </div>
      <div class="p-6 flex justify-end gap-4 items-center">
        <template v-if="alreadyResponsed">
          <LinkButton buttonText="Kembali" url="/order" linkType="internal" />
        </template>
        <template v-else>
          <div class="w-44">
            <CustomSelect :options="rejectReasons" v-model="selectedReason" :default="selectedReason" placeholder="Tolak" />
          </div>
          <div class="w-44">
            <Button buttonText="Proses" size="big_full" @action="toggleConfirmModal('proceed')" />
          </div>
        </template>
      </div> -->
    </div>
    <Modal :modalVisible="visibleConfirmModal" @close="toggleConfirmModal()" width="600px" id="confirmation-modal">
      <template slot="modal-content">
        <div class="flex items-center justify-start gap-6 mb-6">
          <span class="flex-shrink-0">
            <Alert color="neutral-700" />
          </span>
          <div class="flex-col flex gap-2 items-start">
            <p class="text-2xl font-bold">{{ $t('order.orderDetail.statusChange') }}</p>
            <p class="text-sm text-neutral-500 text-left">
              {{ $t('order.orderDetail.statusDesc') }}
            </p>
          </div>
        </div>
        <div class="flex justify-end gap-4">
          <Button buttonText="Tidak" type="secondary" @action="toggleConfirmModal()" :size="isMobile ? 'full' : 'regular'" />
          <Button buttonText="Ya" type="primary" @action="proceed()" :size="isMobile ? 'full' : 'regular'" />
        </div>
      </template>
    </Modal>
    <Modal :modalVisible="visibleAddCostModal" @close="toggleAddCostModal()" width="660px" id="add-cost-modal">
      <template slot="modal-content">
        <p class="text-2xl font-bold mb-6 text-neutral-700 text-left">{{ $t('order.orderDetail.underpaid') }}</p>
        <div class="flex items-center gap-6 text-left mb-6">
          <div class="flex-1">
            <label class="text-sm text-neutral-700 font-medium mb-1 inline-block">{{ $t('order.orderDetail.length') }}</label>
            <div class="relative">
              <TextField borderEnabled type="number" v-model="packageLength" />
              <span class="absolute text-neutral-700 right-3 top-1/2 transform -translate-y-1/2">cm</span>
            </div>
          </div>
          <div class="flex-1">
            <label class="text-sm text-neutral-700 font-medium mb-1 inline-block">{{ $t('order.orderDetail.width') }}</label>
            <div class="relative">
              <TextField borderEnabled type="number" v-model="packageWidth" />
              <span class="absolute text-neutral-700 right-3 top-1/2 transform -translate-y-1/2">cm</span>
            </div>
          </div>
          <div class="flex-1">
            <label class="text-sm text-neutral-700 font-medium mb-1 inline-block">{{ $t('order.orderDetail.height') }}</label>
            <div class="relative">
              <TextField borderEnabled type="number" v-model="packageHeight" />
              <span class="absolute text-neutral-700 right-3 top-1/2 transform -translate-y-1/2">cm</span>
            </div>
          </div>
        </div>
        <div class="text-left w-1/3 mb-10">
          <label class="text-sm text-neutral-700 font-medium mb-1 inline-block">{{ $t('order.orderDetail.additionalPrice') }}</label>
          <div class="relative">
            <TextField borderEnabled type="number" v-model="packagePrice" withIcon />
            <span class="absolute text-neutral-700 left-3 top-1/2 transform -translate-y-1/2">Rp</span>
          </div>
        </div>
        <div class="flex justify-end gap-4">
          <Button buttonText="Tutup" type="secondary" @action="toggleAddCostModal()" :size="isMobile ? 'full' : 'regular'" />
          <Button buttonText="Submit" type="primary" @action="rejectAddCost()" :size="isMobile ? 'full' : 'regular'" />
        </div>
      </template>
    </Modal>
    <Modal :modalVisible="visibleOtherModal" @close="toggleOtherModal()" width="600px" id="confirmation-modal">
      <template slot="modal-content">
        <div class="p-1">
          <p class="text-2xl font-bold mb-6 text-neutral-700 text-left">{{ $t('order.orderDetail.other') }}</p>
          <div class="mb-6">
            <Textarea v-model="otherReason" placeholder="Silakan tulis alasannya" borderEnabled :error="otherReasonError" />
          </div>
          <div class="flex justify-end gap-4">
            <Button buttonText="Tutup" type="secondary" @action="toggleOtherModal()" :size="isMobile ? 'full' : 'regular'" />
            <Button buttonText="Submit" type="primary" @action="rejectOther()" :size="isMobile ? 'full' : 'regular'" />
          </div>
        </div>
      </template>
    </Modal>
    <Modal :modalVisible="visiblePreviewReceipt" @close="previewReceipt()" width="700px" id="receipt-modal">
      <template slot="modal-content">
        <Receipt :orderDetail="orderDetail" @close="previewReceipt()"></Receipt>
      </template>
    </Modal>
    <Modal :modalVisible="visibleComplainModal" @close="toggleComplainModal()" width="600px" id="complain-modal">
      <template slot="modal-content">
        <Complain @close="toggleComplainModal()" :orderId="orderId"></Complain>
      </template>
    </Modal>
    <Modal :modalVisible="visibleModalShippingLabel" @close="toggleModalShippingLabel()" width="600px" id="shipping-label-modal">
      <template slot="modal-content">
        <div class="flex items-center justify-start gap-6 mb-6">
          <div class="flex-col flex gap-2 items-start">
            <p class="text-2xl font-bold">{{ $t('order.orderDetail.deliveryLabel') }}</p>
            <p class="text-sm text-neutral-500 text-left">
              {{ $t('order.orderDetail.downloadLabelText') }}
            </p>
          </div>
        </div>
        <div class="flex justify-end gap-4">
          <Button buttonText="Tidak" type="secondary" @action="toggleModalShippingLabel()" :size="isMobile ? 'full' : 'regular'" />
          <Button buttonText="Download" type="primary" @action="downloadAndClose()" :size="isMobile ? 'full' : 'regular'" />
        </div>
      </template>
    </Modal>

    <CancelOrderModal :modal="visibleModalCancelOrder" @toggle="toggleModalCancelOrder()" @closeAll="closeAll()" />

    <Modal :modalVisible="visiblePriceAdjustmentModal" @close="togglePriceAdjustmentModal()" width="600px" id="price-adjustment-modal">
      <template slot="modal-content">
        <div class="flex items-center justify-start gap-6 mb-6">
          <span class="flex-shrink-0">
            <Alert color="neutral-700" />
          </span>
          <div class="flex-col flex gap-2 items-start">
            <p class="text-2xl font-bold">{{ $t('order.orderDetail.markAsPaidConfirmTitle') }}</p>
            <p class="text-sm text-neutral-500 text-left">
              {{ $t('order.orderDetail.markAsPaidConfirmDesc') }}
            </p>
          </div>
        </div>
        <div class="flex justify-end gap-4">
          <Button buttonText="Tidak" type="secondary" @action="togglePriceAdjustmentModal()" :size="isMobile ? 'full' : 'regular'" />
          <Button buttonText="Ya" type="primary" @action="handlePriceAdjustmentAction()" :size="isMobile ? 'full' : 'regular'" />
        </div>
      </template>
    </Modal>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import CancelOrderModal from './CancelOrderModal.vue'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

export default {
  filters: {
    formatCurrency(value) {
      if (!value) return '0'
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
    }
  },
  components: {
    LogisticPlaceholder: () => import(/* webpackChunkName: "LogisticPlaceholder" */ '@/components/Icons/LogisticPlaceholder'),
    // Info: () => import(/* webpackChunkName: "Icons" */ '@/components/Icons/Info'),
    Button: () => import(/* webpackChunkName: "Forms" */ '@/components/Forms/Button'),
    TextField: () => import(/* webpackChunkName: "Forms" */ '@/components/Forms/TextField'),
    Textarea: () => import(/* webpackChunkName: "Forms" */ '@/components/Forms/Textarea'),
    // LinkButton: () => import(/* webpackChunkName: "Forms" */ '@/components/Forms/LinkButton'),
    // CustomSelect: () => import(/* webpackChunkName: "Forms" */ '@/components/Forms/CustomSelect'),
    Alert: () => import(/* webpackChunkName: "Icons" */ '@/components/Icons/Alert'),
    Modal: () => import(/* webpackChunkName: "Modal" */ '@/components/Modal/Modal'),
    Receipt: () => import(/* webpackChunkName: "Receipt" */ '@/components/Receipt/Receipt'),
    Info: () => import(/* webpackChunkName: "Icons" */ '@/components/Icons/Info'),
    Complain: () => import(/* webpackChunkName: "Complain" */ '@/components/Complain/Complain'),
    CancelOrderModal
  },
  data() {
    return {
      otherReason: null,
      packageLength: 0,
      packageWidth: 0,
      packageHeight: 0,
      packagePrice: 0,
      selectedApprovalStatus: null,
      visibleConfirmModal: false,
      status: {
        PYMWAI: 'text-neutral-500 bg-neutral-300',
        PYMCNF: 'text-white bg-system-warning',
        DLVD: 'text-white bg-system-success',
        CNC: 'text-white bg-system-error',
        HUBPRCD: 'text-white bg-system-warning',
        HUBRJCT: 'text-white bg-system-error',
        OCNCL: 'text-white bg-red-500'
      },
      deliveryServiceTypeList: [
        {
          name: 'Pickup and Dropoff',
          value: 'A'
        },
        {
          name: 'Pickup Only',
          value: 'P'
        },
        {
          name: 'Dropoff Only',
          value: 'D'
        }
      ],
      rejectReasons: ['Barang Terlarang', 'Kurang Bayar', 'Lainnya'],
      selectedReason: null,
      visibleAddCostModal: false,
      visibleOtherModal: false,
      otherReasonError: null,
      visiblePreviewReceipt: false,
      visibleComplainModal: false,
      visibleModalShippingLabel: false,
      currentTime: Date.now() + 7 * 60 * 60 * 1000,
      visibleModalCancelOrder: false,
      visiblePriceAdjustmentModal: false
    }
  },
  computed: {
    ...mapGetters('order', ['orderDetail']),
    ...mapGetters('common', ['isMobile']),
    orderId() {
      return this.$route.params.id
    },
    statusClass() {
      return this.status[this.orderDetail.status]
    },
    alreadyResponsed() {
      return ['HUBRJCT', 'HUBPRCD'].indexOf(this.orderDetail.status) > -1
    },
    isWithin30Minutes() {
      if (this.orderDetail && this.orderDetail.created_time) {
        return new Date(this.orderDetail.created_time).getTime() + 30 * 1000 > this.currentTime
      }
      return false
    },
    priceBeforeDiscount() {
      if (this.orderDetail?.payment_info?.voucher_info) {
        const currentPrice = this.orderDetail.payment_info.price || 0
        const voucherAmount = this.orderDetail.payment_info.voucher_info.voucher_amount || 0
        return currentPrice + voucherAmount
      }
      return this.orderDetail?.payment_info?.price || 0
    }
  },
  created() {
    // this.getOrderInfo()
    if (this.orderDetail.status === 'OPRCD' || (this.orderDetail.status === 'OHUBCEK' && this.isWithin30Minutes)) {
      this.visibleModalShippingLabel = true
    }
  },
  watch: {
    selectedReason(val) {
      if (val) this.toggleConfirmModal('reject')
    },
    'orderDetail.created_time': function(newVal) {
      // Update modal visibility when created_time changes
      if (
        (this.orderDetail.status === 'OPRCD' && new Date(newVal).getTime() + 30 * 60 * 1000 > this.currentTime) ||
        (this.orderDetail.status === 'OHUBCEK' && new Date(newVal).getTime() + 30 * 60 * 1000 > this.currentTime)
      ) {
        this.visibleModalShippingLabel = true
      } else {
        this.visibleModalShippingLabel = false
      }
    },
    '$route.params.id': {
      handler(newId, oldId) {
        if (newId) {
          this.getOrderInfo()
        }
      },
      immediate: true
    }
  },
  methods: {
    ...mapActions('order', ['getOrderDetail', 'processOrder', 'rejectOrder', 'retryPayment', 'downloadShippingLabel', 'updatePriceAdjustmentStatus']),
    ...mapActions('common', ['showLoading', 'hideLoading', 'setToast']),
    toggleComplainModal() {
      // this.visibleComplainModal = !this.visibleComplainModal
      window.open('https://api.whatsapp.com/send?phone=6285285890090')
    },
    previewReceipt() {
      this.visiblePreviewReceipt = !this.visiblePreviewReceipt
    },
    canCancelOrder() {
      const status = this.orderDetail?.status
      if (status === 'OPAYWAIT') return true
      if (status === 'OCNCL') return false

      if (this.orderDetail.payment_info) {
        const pi = this.orderDetail.payment_info
        const dateNow = dayjs().format('YYYY-MM-DD HH:mm:ss')
        let maxCancelTime = dateNow

        if (pi && pi.status === 'settlement' && pi.settlement_time && !['Transfer Bank', 'Mandiri Payment'].includes(pi.payment_method)) {
          maxCancelTime = dayjs
            .tz(pi.settlement_time, 'Asia/Jakarta')
            .add(30, 'minute')
            .format('YYYY-MM-DD HH:mm:ss')
        } else if (pi && pi.payment_method == 'Invoice') {
          maxCancelTime = dayjs
            .tz(this.orderDetail.created_time, 'Asia/Jakarta')
            .add(30, 'minute')
            .format('YYYY-MM-DD HH:mm:ss')
        }

        if (maxCancelTime >= dateNow) {
          return true
        }
      }
      return false
    },
    download() {
      this.showLoading()
      this.downloadShippingLabel({
        orderId: this.orderId
      }).then(response => {
        this.hideLoading()
        const url = URL.createObjectURL(response.data)
        const a = document.createElement('a')
        a.download = this.orderDetail.order_delivery?.awb + '.pdf'
        a.href = url
        a.target = '_self'

        a.click()

        setTimeout(function() {
          a.remove()
          URL.revokeObjectURL(url)
        }, 100)
      })
    },
    downloadAndClose() {
      this.download()
      this.toggleModalShippingLabel()
    },
    getVolumetricWeight(dimension) {
      if (!dimension) return 0
      const dims = dimension.split('x')
      if (dims.length === 3) {
        const p = parseFloat(dims[0])
        const l = parseFloat(dims[1])
        const t = parseFloat(dims[2])
        return Math.ceil((p * l * t) / 6000)
      }
      return 0
    },
    doRetryPayment() {
      this.showLoading()
      this.retryPayment({
        orderId: this.orderId
      }).then(res => {
        this.hideLoading()
        this.handlePay(res.data.data)
      })
    },
    handlePay(res) {
      const self = this
      const response = res
      window.snap.pay(response.payment_token, {
        onSuccess: function(result) {
          console.log('success')
          console.log(result)
          self.showLoading()
          setTimeout(() => {
            self.$router.push(`/order`)
            self.hideLoading()
          }, 1000)
        },
        onPending: function(result) {
          console.log('pending')
          console.log(result)
          self.showLoading()
          setTimeout(() => {
            self.$router.push(`/order`)
            self.hideLoading()
          }, 1000)
        },
        onError: function(result) {
          console.log('error')
          console.log(result)
          self.showLoading()
          setTimeout(() => {
            self.$router.push(`/order`)
            self.hideLoading()
          }, 1000)
        },
        onClose: function() {
          console.log('customer closed the popup without finishing the payment')
        }
      })
    },
    toggleAddCostModal() {
      this.visibleAddCostModal = !this.visibleAddCostModal
      if (!this.visibleAddCostModal) {
        this.selectedReason = null
      }
    },
    toggleOtherModal() {
      this.visibleOtherModal = !this.visibleOtherModal
      if (!this.visibleOtherModal) {
        this.selectedReason = null
      }
    },
    toggleConfirmModal(status) {
      if (status) {
        this.selectedApprovalStatus = status
      }
      this.visibleConfirmModal = !this.visibleConfirmModal
      if (!this.visibleConfirmModal && this.selectedApprovalStatus === 'reject') {
        this.selectedReason = null
      }
    },
    toggleModalShippingLabel() {
      this.visibleModalShippingLabel = !this.visibleModalShippingLabel
    },
    toggleModalCancelOrder() {
      this.visibleModalCancelOrder = !this.visibleModalCancelOrder
    },
    closeAll() {
      this.visibleModalCancelOrder = false
    },
    togglePriceAdjustmentModal() {
      this.visiblePriceAdjustmentModal = !this.visiblePriceAdjustmentModal
    },
    handlePriceAdjustmentAction() {
      this.visiblePriceAdjustmentModal = false
      this.showLoading()
      this.updatePriceAdjustmentStatus({
        orderId: this.orderId,
        payloads: {
          status: 'Payment Under Verification'
        }
      })
        .then(() => {
          this.hideLoading()
          this.setToast({
            options: {
              type: 'success',
              duration: 1000,
              primaryText: 'Payment Marked as Paid',
              secondaryText: 'Price adjustment status has been updated to Payment Under Verification.',
              callback: () => {
                this.getOrderInfo()
              }
            }
          })
        })
        .catch(() => {
          this.hideLoading()
          this.setToast({
            options: {
              type: 'error',
              duration: 1000,
              primaryText: 'Failed to Update',
              secondaryText: 'Failed to mark payment as paid.'
            }
          })
        })
    },
    rejectOther() {
      this.otherReasonError = null
      if (this.otherReason) {
        this.visibleAddCostModal = false
        this.showLoading()
        this.rejectOrder({
          orderId: this.orderId,
          payloads: {
            reject_type: 'OTHERS',
            reason: this.otherReason
          }
        }).then(() => {
          this.hideLoading()
          this.setToast({
            options: { type: 'success', duration: 1000, primaryText: 'Order Status telah berubah', secondaryText: 'Data telah terimpan dalam sistem.', callback: this.handleCallback }
          })
        })
      } else {
        this.otherReasonError = 'Tuliskan terlebih dahulu alasannya!'
      }
    },
    rejectAddCost() {
      this.visibleAddCostModal = false
      this.showLoading()
      this.rejectOrder({
        orderId: this.orderId,
        payloads: {
          reject_type: 'ADDCOST',
          reason: 'Kurang Bayar',
          modified_package_size: {
            dimension: `${this.packageLength}x${this.packageWidth}x${this.packageHeight}`,
            addition_cost: parseInt(this.packagePrice)
          }
        }
      }).then(() => {
        this.hideLoading()
        this.setToast({
          options: { type: 'success', duration: 1000, primaryText: 'Order Status telah berubah', secondaryText: 'Data telah terimpan dalam sistem.', callback: this.handleCallback }
        })
      })
    },
    proceed() {
      this.visibleConfirmModal = false
      if (this.selectedApprovalStatus === 'proceed') {
        this.showLoading()
        this.processOrder({
          orderId: this.orderId
        }).then(() => {
          this.hideLoading()
          this.setToast({
            options: { type: 'success', duration: 1000, primaryText: 'Order Status telah berubah', secondaryText: 'Data telah terimpan dalam sistem.', callback: this.handleCallback }
          })
        })
      } else {
        if (this.selectedReason === 'Barang Terlarang') {
          this.showLoading()
          this.rejectOrder({
            orderId: this.orderId,
            payloads: {
              reject_type: 'DANGGOOD',
              reason: 'Barang Terlarang'
            }
          }).then(() => {
            this.hideLoading()
            this.setToast({
              options: { type: 'success', duration: 1000, primaryText: 'Order Status telah berubah', secondaryText: 'Data telah terimpan dalam sistem.', callback: this.handleCallback }
            })
          })
        } else if (this.selectedReason === 'Kurang Bayar') {
          this.visibleAddCostModal = true
        } else if (this.selectedReason === 'Lainnya') {
          this.visibleOtherModal = true
        }
      }
    },
    handleCallback() {
      this.$router.push('/order')
    },
    reverseObject(array) {
      let newArr = Object.assign([], array)
      return newArr && newArr.reverse()
    },
    getServiceType(value) {
      return this.deliveryServiceTypeList.filter(item => item.value === value)[0]?.name
    },
    getOrderInfo() {
      this.showLoading()
      this.getOrderDetail({
        orderId: this.orderId
      }).then(() => {
        this.hideLoading()
      })
    }
  }
}
</script>
<style></style>
